import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import Button from '../../components/Button/Button';
import Modal, { ModalActions, ModalSubtitle, ModalTitle } from '../../components/Modal/Modal';
import { navItemIcon } from '../../components/Sidebar/Sidebar';
import { useToast } from '../../components/Toast/ToastContext';
import { useRescueContext } from '../../lib/RescueContext';
import { SUPPORT_EMAIL } from '../../lib/emails';
import {
  type DevicesWaitlist as DevicesWaitlistEntry,
  type RescueGwaDashboardStatsData,
  rescueGwaDashboardStatsQuery,
  rescueJoinDevicesWaitlistMutation,
  rescueLeaveDevicesWaitlistMutation,
} from '../../lib/rescueQueries';
import { formatDate } from '../../lib/utils';
import devicesHero from '../../assets/devices-hero.jpg';
import styles from './DevicesWaitlist.module.scss';

interface DevicesWaitlistProps {
  waitlist: DevicesWaitlistEntry | null;
  onChanged: () => void;
}

function errorMessage(err: any) {
  return err?.graphQLErrors?.[0]?.message || err?.message || 'Something went wrong';
}

export default function DevicesWaitlist({ waitlist, onChanged }: DevicesWaitlistProps) {
  const { showToast } = useToast();
  const { isReseller } = useRescueContext();
  const { data: gwaData } = useQuery<RescueGwaDashboardStatsData>(rescueGwaDashboardStatsQuery);
  const inGwa = !!gwaData?.rescueGwaProgram;
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [joinWaitlist, { loading: joining }] = useMutation(rescueJoinDevicesWaitlistMutation);
  const [leaveWaitlist, { loading: leaving }] = useMutation(rescueLeaveDevicesWaitlistMutation);

  async function handleJoin() {
    try {
      await joinWaitlist();
      showToast('You’ve joined the Fi Devices waitlist');
      onChanged();
    } catch (err) {
      showToast(`Couldn’t join the waitlist: ${errorMessage(err)}`);
    }
  }

  async function handleLeave() {
    try {
      await leaveWaitlist();
      setLeaveOpen(false);
      showToast('You’ve left the Fi Devices waitlist');
      onChanged();
    } catch (err) {
      showToast(`Couldn’t leave the waitlist: ${errorMessage(err)}`);
    }
  }

  return (
    <>
      <div className={styles.pageHeader}>
        <h1>Fi Devices</h1>
      </div>

      <section className={styles.hero}>
        <div className={styles.heroBody}>
          <h2 className={styles.heroTitle}>
            {waitlist ? 'You’re on the Fi Devices waitlist' : 'Join the Fi Devices waitlist'}
          </h2>
          <p className={styles.heroText}>
            Put a GPS tracker on dogs in your care and give adopters a special rescue‑only offer. We’re adding rescues
            as device capacity opens.
          </p>
          {waitlist ? (
            <>
              <div className={styles.statusRow}>
                <span className={styles.statusPill}>&#10003; On the waitlist</span>
                <span className={styles.statusMeta}>
                  Joined {formatDate(waitlist.joinedAt)}
                  {waitlist.joinedByName && ` by ${waitlist.joinedByName}`}
                </span>
              </div>
              <p className={styles.emailNote}>
                We’ll email you when your rescue is selected.{' '}
                <button type="button" className={styles.leaveLink} onClick={() => setLeaveOpen(true)}>
                  Leave waitlist
                </button>
              </p>
            </>
          ) : (
            <div className={styles.statusRow}>
              <Button variant="green" onClick={handleJoin} disabled={joining}>
                {joining ? 'Joining…' : 'Join waitlist'}
              </Button>
              <span className={styles.statusMeta}>We’ll email you when your rescue is selected.</span>
            </div>
          )}
        </div>
        <div className={styles.heroImage}>
          <img src={devicesHero} alt="" />
        </div>
      </section>

      <section className={styles.selection}>
        <h2 className={styles.sectionTitle}>How rescues are selected</h2>
        <p className={styles.sectionText}>
          We choose rescues as device capacity opens. Strong results in Fi Nano Microchips and Gift with Adoption help
          your rescue qualify.
        </p>
        <div className={styles.programs}>
          <ProgramCard
            iconKey="chips"
            name="Fi Nano Microchips"
            path="/rescue/chips"
            enrolled={isReseller}
            indicators={[
              {
                title: 'Chip registration rate',
                description: 'The share of chips you’ve received that are registered to an adopter.',
              },
            ]}
          />
          <ProgramCard
            iconKey="gwa"
            name="Gift with Adoption"
            path="/rescue/gwa"
            enrolled={inGwa}
            indicators={[
              { title: 'Flyer redemption rate', description: 'Redemptions for every 100 flyers you send out.' },
              { title: 'Digital redemptions', description: 'Adopters who redeem your digital offer.' },
            ]}
          />
        </div>
      </section>

      <p className={styles.support}>
        Have questions?{' '}
        <a href={`mailto:${SUPPORT_EMAIL}?subject=Fi%20Devices%20Waitlist%20Questions`}>Contact support</a>
      </p>

      <Modal open={leaveOpen} onClose={() => setLeaveOpen(false)}>
        <ModalTitle>Leave the Fi Devices waitlist?</ModalTitle>
        <ModalSubtitle>
          Your rescue won’t be considered for Fi Devices. If you rejoin later, you’ll go to the back of the waitlist.
        </ModalSubtitle>
        <ModalActions>
          <Button variant="secondary" onClick={() => setLeaveOpen(false)} disabled={leaving}>
            Stay on waitlist
          </Button>
          <Button variant="danger" onClick={handleLeave} disabled={leaving}>
            {leaving ? 'Leaving…' : 'Leave waitlist'}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
}

interface ProgramCardProps {
  iconKey: string;
  name: string;
  path: string;
  enrolled: boolean;
  indicators: { title: string; description: string }[];
}

function ProgramCard({ iconKey, name, path, enrolled, indicators }: ProgramCardProps) {
  const history = useHistory();
  return (
    <div className={styles.programCard}>
      <div className={styles.programHeader}>
        <span className={styles.programIcon}>{navItemIcon(iconKey)}</span>
        <h3 className={styles.programName}>{name}</h3>
        <Button
          variant={enrolled ? 'secondary' : 'greenOutline'}
          size="sm"
          className={styles.programCta}
          onClick={() => history.push(path)}
        >
          {enrolled ? `Go to ${name} →` : `Join ${name}`}
        </Button>
      </div>
      {indicators.map((indicator) => (
        <div key={indicator.title} className={styles.indicator}>
          <div className={styles.indicatorTitle}>{indicator.title}</div>
          <div className={styles.indicatorText}>{indicator.description}</div>
        </div>
      ))}
    </div>
  );
}
